/**
 * QuickCreatePopover — Popover do botão "Novo" (Quick Create)
 *
 * Estrutura idêntica ao App Launcher:
 *   - Campo de pesquisa (filtra a lista completa por label).
 *   - Cabeçalho "Seus favoritos" com toggle de edição (jiggle + remover).
 *   - Grade de favoritos (empty state quando o usuário não configurou).
 *   - Divisor + lista completa "Todas as ações" agrupada por categorias.
 *   - Estrela na lista completa alterna o estado de favorito.
 *
 * Ao ativar uma ação, navega para `route?new=<newParam>` — a página destino
 * usa useAutoOpenFromQuery para abrir o modal automaticamente.
 *
 * RELEASE: 2026-07-24 v1.2.0 - Campo de pesquisa no topo. Quando preenchido,
 *          oculta favoritos/categorias e exibe grade única de resultados.
 * RELEASE: 2026-07-24 v1.1.0 - Scroll unificado do popover inteiro.
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Check, Plus, Search, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAvailableQuickCreates } from '@/hooks/useAvailableQuickCreates';
import { useQuickCreatePrefs } from '@/hooks/useQuickCreatePrefs';
import { QuickCreateTile } from './QuickCreateTile';
import {
  QUICK_CREATE_BY_KEY,
  QUICK_CREATE_CATEGORIES,
  type QuickCreateDef,
} from '@/config/quickCreate';

interface QuickCreatePopoverProps {
  onClose: () => void;
}

/** Normaliza string para comparação (lowercase + remove acentos). */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

export function QuickCreatePopover({ onClose }: QuickCreatePopoverProps) {
  const navigate = useNavigate();
  const available = useAvailableQuickCreates();
  const { favorites, isFavorite, toggleFavorite } = useQuickCreatePrefs();
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState('');

  const availableKeys = useMemo(
    () => new Set(available.map((a) => a.key)),
    [available],
  );

  const favoriteActions = useMemo(() => {
    return favorites
      .filter((k) => availableKeys.has(k) && QUICK_CREATE_BY_KEY[k])
      .map((k) => QUICK_CREATE_BY_KEY[k]);
  }, [favorites, availableKeys]);

  const searchTerm = search.trim();
  const isSearching = searchTerm.length > 0;

  // Resultado da busca: lista plana filtrada por label normalizado.
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = normalize(searchTerm);
    return available.filter((a) => normalize(a.label).includes(q));
  }, [available, isSearching, searchTerm]);

  const handleActivate = (action: QuickCreateDef) => {
    const sep = action.route.includes('?') ? '&' : '?';
    navigate(`${action.route}${sep}new=${encodeURIComponent(action.newParam)}`);
    onClose();
  };

  return (
    <div className="flex h-[min(68dvh,var(--radix-popover-content-available-height,68dvh))] max-h-[68vh] w-[min(92vw,22rem)] flex-col overflow-hidden">
      {/* Campo de pesquisa (fixo no topo, fora do ScrollArea) */}
      <div className="border-b border-border/60 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar ação..."
            autoFocus
            className="h-8 bg-muted/40 pl-7 pr-7 text-xs"
          />
          {isSearching && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <ScrollArea
        axis="y"
        touchAxis="pan-y"
        preventScrollChaining
        reserveScrollbarSpace
        className="h-full min-h-0 flex-1"
      >
        {isSearching ? (
          /* Modo busca: apenas grade única de resultados */
          <div className="px-2 pb-2 pt-2">
            <div className="px-1 pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Resultados ({searchResults.length})
              </p>
            </div>
            {searchResults.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-6 text-center text-xs text-muted-foreground">
                Nenhuma ação encontrada para "{searchTerm}".
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {searchResults.map((a) => (
                  <QuickCreateTile
                    key={a.key}
                    action={a}
                    onActivate={handleActivate}
                    onToggleFavorite={toggleFavorite}
                    isFavorite={isFavorite(a.key)}
                    editMode={false}
                    showFavoriteToggle
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Cabeçalho */}
            <div className="flex items-center justify-between px-3 pt-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Plus className="h-3.5 w-3.5" />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">Seus favoritos</p>
                  <p className="text-[11px] text-muted-foreground">
                    Ações rápidas de criação que você mais usa
                  </p>
                </div>
              </div>
              {favoriteActions.length > 0 && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setEditMode((v) => !v)}
                  aria-label={editMode ? 'Concluir edição' : 'Editar favoritos'}
                >
                  {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>

            {/* Favoritos */}
            <div className="px-2 pt-2">
              {favoriteActions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
                  Nenhum favorito ainda. Clique na estrela em qualquer ação abaixo
                  para adicioná-la aqui.
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-1">
                  {favoriteActions.map((a) => (
                    <QuickCreateTile
                      key={a.key}
                      action={a}
                      onActivate={handleActivate}
                      onToggleFavorite={toggleFavorite}
                      isFavorite
                      editMode={editMode}
                      showFavoriteToggle={false}
                    />
                  ))}
                </div>
              )}
            </div>

            <Separator className="my-3" />

            <div className="px-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Todas as ações
              </p>
            </div>

            <div className="px-2 pb-2 pt-1">
              {available.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  Nenhuma ação disponível para o seu perfil.
                </div>
              ) : (
                <div className="space-y-3 pr-2">
                  {QUICK_CREATE_CATEGORIES.map((cat) => {
                    const items = cat.actionKeys
                      .filter((k) => availableKeys.has(k) && QUICK_CREATE_BY_KEY[k])
                      .map((k) => QUICK_CREATE_BY_KEY[k]);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat.key} className="space-y-1">
                        {cat.title && (
                          <p className="px-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {cat.title}
                          </p>
                        )}
                        <div className="grid grid-cols-4 gap-1">
                          {items.map((a) => (
                            <QuickCreateTile
                              key={a.key}
                              action={a}
                              onActivate={handleActivate}
                              onToggleFavorite={toggleFavorite}
                              isFavorite={isFavorite(a.key)}
                              editMode={false}
                              showFavoriteToggle
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </ScrollArea>
    </div>
  );
}
